import type { CreateProjectV2RequestDto } from '@/lib/api/v2/projects'

/**
 * SOLY-REGIE-001 MS-03 — the payload of « Nouvelle intervention », as a pure function.
 *
 * It carries the decisions, so it is what deserves tests: which `isDirectSale` each button sends,
 * where the VAT comes from, and how a client/job-site that may or may not exist yet is expressed in
 * the wire modes. The screen around it is plumbing.
 *
 * No new endpoint: `POST /api/v2/projects` already handles existing/new customer, none/existing/new
 * job site and duplicate detection. The short form is a different QUESTION asked of the same door.
 */

/** Construction type → VAT, Belgian rules. Deliberately duplicated from the wizard rather than
 *  shared: the day one of the two screens gets its own list, a shared constant would make the other
 *  one change in silence. Three values, one line each. */
export const INTERVENTION_CONSTRUCTION_TYPES = [
  { value: 'new_construction', label: 'Construction neuve', vatPercent: 21 },
  { value: 'renovation', label: 'Rénovation', vatPercent: 6 },
  { value: 'co_contractor', label: 'Co-contractant', vatPercent: 0 },
] as const

export type InterventionExit = 'plan' | 'quote'

/** Civility codes as the backend enum orders them. Only the two a retouche ever needs. */
export const CIVILITY = { mr: 1, mrs: 2 } as const
export type CivilityChoice = keyof typeof CIVILITY

export interface InterventionFormState {
  /** Existing client, or null when the form creates one. */
  customerId: string | null
  newCustomer: { lastName: string; phone: string; civility: CivilityChoice | null } | null
  /** Existing job site, or null when the form creates one (or when there is none yet). */
  jobSiteId: string | null
  newAddress: { street: string; number: string; postalCode: string; city: string } | null
  /** « Ce qu'il faut faire » — becomes the project name, which is what she reads in every list. */
  description: string
  /** One of INTERVENTION_CONSTRUCTION_TYPES.value. No default: it is a money decision. */
  constructionType: string
  allowDuplicate?: boolean
}

/** The VAT the chosen construction type implies, or null when nothing is chosen yet. */
export function vatPercentFor(constructionType: string): number | null {
  return INTERVENTION_CONSTRUCTION_TYPES.find((o) => o.value === constructionType)?.vatPercent ?? null
}

/**
 * What the form is still waiting for, in the words she uses — empty when it can be submitted.
 *
 * A client (existing, or new with a name and a civility), somewhere to go, a description and a
 * construction type. Nothing else is required, and the duration deliberately is not
 * (SOLY-PLAN-004: an estimate nobody typed is not an estimate).
 *
 * QA staging 2026-08-20 — the two exit buttons went grey with NOTHING saying why. She has the
 * client on the phone, she typed four fields, she clicks, and nothing happens. The blocker is
 * often the job-site radio, which is the least visible control on the screen.
 *
 * This is the single source of truth: `canSubmitIntervention` is now defined AS "nothing is
 * missing", so the message and the button can never drift apart. Adding a rule to one adds it to
 * the other.
 */
export function missingForIntervention(state: InterventionFormState): string[] {
  const missing: string[] = []

  // C4 (QA 2026-08-19) — la civilité est EXIGÉE sur un client neuf, elle n'est pas devinée. Écrire
  // « M. » par défaut n'est pas une absence de donnée mais une donnée FAUSSE, imprimée sur les
  // documents de ~300 clientes par an. Un clic contre une erreur visible par le client : le clic
  // gagne. (`Mx` n'est pas une échappatoire : il s'imprime littéralement « Mx » en fr-BE.)
  if (state.customerId == null) {
    if ((state.newCustomer?.lastName.trim().length ?? 0) === 0) missing.push('le nom du client')
    else if (state.newCustomer?.civility == null) missing.push('la civilité (M. ou Mme)')
  }

  if (state.jobSiteId == null) {
    const a = state.newAddress
    if (a == null || (a.street.trim() === '' && a.postalCode.trim() === '' && a.city.trim() === '')) {
      missing.push("l'adresse du chantier")
    } else {
      // C3 (QA 2026-08-19) — « la rue seule » n'est pas une adresse. Sans code postal ni localité,
      // ni l'Inbox ni la barre d'agenda ne disent plus OÙ aller, et la facture part incomplète.
      // C'est le champ qu'on oublie quand on a le client au téléphone, donc c'est celui qu'il
      // faut exiger.
      if (a.street.trim() === '') missing.push('la rue')
      if (a.postalCode.trim() === '') missing.push('le code postal')
      if (a.city.trim() === '') missing.push('la localité')
    }
  }

  if (state.description.trim().length === 0) missing.push("ce qu'il faut faire")
  if (vatPercentFor(state.constructionType) == null) missing.push('le type de construction')

  return missing
}

export function canSubmitIntervention(state: InterventionFormState): boolean {
  return missingForIntervention(state).length === 0
}

export function buildInterventionPayload(
  state: InterventionFormState,
  exit: InterventionExit,
): CreateProjectV2RequestDto {
  const vatPercent = vatPercentFor(state.constructionType)

  return {
    // An intervention is neither a kitchen nor a dressing: the legacy project type does not apply,
    // and the backend stops requiring it for this nature.
    projectTypeId: '00000000-0000-0000-0000-000000000000',
    nature: 'Intervention',
    salesOwnerId: null,
    allowDuplicate: state.allowDuplicate ?? false,
    projectName: state.description.trim(),
    // The exit button decides the commercial model, not a radio (décision Thierry 2026-08-18):
    //   [Planifier]      → direct sale: the Draft order is created at once and the price is filled
    //                      after the passage, which is the 55 % case.
    //   [Faire un devis] → quote path: a deposit schedule is created from the org template.
    // ⚠️ It is one-way — SalesOrderController refuses the order editor on a project that is not a
    // direct sale. A file opened by [Faire un devis] can no longer be invoiced as a direct sale.
    isDirectSale: exit === 'plan' ? true : undefined,
    vatPercent,
    customer: state.customerId != null
      ? { mode: 1, customerId: state.customerId, new: undefined }
      : {
          mode: 2,
          customerId: null,
          new: {
            type: 1, // Individual — a retouche is a private client until told otherwise
            name: state.newCustomer?.lastName.trim() || null,
            designation: null,
            vatNumber: null,
            companyRegistrationNumber: null,
            preferredLanguageId: 'fr-BE',
            primaryContact: {
              civility: state.newCustomer?.civility ? CIVILITY[state.newCustomer.civility] : CIVILITY.mr,
              firstName: null,
              lastName: state.newCustomer?.lastName.trim() ?? '',
              // Name + phone is all she has on the phone. Everything else is completed later,
              // before the invoice — which leaves days, not seconds.
              phone: state.newCustomer?.phone.trim() ?? '',
              email: null,
            },
          },
        },
    jobSite: state.jobSiteId != null
      ? { mode: 1, jobSiteId: state.jobSiteId, new: undefined }
      : state.newAddress != null
        ? {
            mode: 2,
            jobSiteId: null,
            new: {
              label: null,
              address: {
                street: state.newAddress.street.trim(),
                number: state.newAddress.number.trim(),
                box: null,
                floor: null,
                postalCode: state.newAddress.postalCode.trim(),
                city: state.newAddress.city.trim(),
                country: 'BE',
              },
            },
          }
        : { mode: 0, jobSiteId: null, new: undefined },
  }
}

/**
 * Where the user lands once the file exists.
 *
 * Both doors land where the WORK happens, never one step short of what the button promised:
 *
 *  * [Planifier] → `/planning/projects/{id}`, the agenda with the file docked beside it. The
 *    intention is to give a date, and that is the one screen where a date is given.
 *  * [Faire un devis] → the draft editor itself (see `quoteDraftRoute`). This route is only the
 *    FALLBACK, used when the draft could not be created: the file exists either way, so landing on
 *    its page is a graceful degradation, not a dead end. NOT `/configuration`, which the wizard
 *    uses — that screen asks for kitchen finishes an intervention does not have.
 */
export function interventionLandingRoute(projectId: string, exit: InterventionExit): string {
  return exit === 'plan' ? `/planning/projects/${projectId}` : `/projects/${projectId}`
}

/** The shape of a freshly created quote, as far as this module needs to know it. */
export interface CreatedQuoteDraft {
  id: string
  variants?: Array<{ draftVersions?: Array<{ id: string }> }>
}

/**
 * The draft editor of a quote that was just created, or `null` when it carries no draft version.
 *
 * Same shape as `ProjectsIdPage` uses for the complementary quote — the first variant's first
 * draft. Pulled out as a pure function because `null` is the branch that decides whether the user
 * lands in her quote or on a fallback page, and that branch deserves a test.
 */
export function quoteDraftRoute(projectId: string, quote: CreatedQuoteDraft): string | null {
  const draft = quote.variants?.[0]?.draftVersions?.[0]
  return draft ? `/projets/${projectId}/devis-v2/${quote.id}/versions/${draft.id}/edit` : null
}
